import type { Box } from '../api';

/**
 * Pure geometry of the Asistente tools (no DOM, no cornerstone, runs in plain node): the long-axis
 * line an accepted measurement suggestion becomes, the box of a drawn line, and size change.
 */

export interface PixelSpacing {
  row: number;
  column: number;
}

const PERCENT = 100;

/**
 * The long axis of a suggested box as a line in image pixels [x0, y0, x1, y1], through the box centre
 * along its longer side in millimetres (pixels when the image is uncalibrated), so the Length tool
 * measures the same long axis ris reported.
 */
export function longAxisLine(box: Box, columns: number, rows: number, spacing: PixelSpacing | null): [number, number, number, number] {
  const [x0, y0, x1, y1] = [box[0] * columns, box[1] * rows, box[2] * columns, box[3] * rows];
  const widthMm = (x1 - x0) * (spacing?.column ?? 1);
  const heightMm = (y1 - y0) * (spacing?.row ?? 1);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return widthMm >= heightMm ? [x0, cy, x1, cy] : [cx, y0, cx, y1];
}

/** Normalised bounding box of a line drawn in image pixels (a degenerate side gets one pixel). */
export function boxOfLine(points: readonly number[], columns: number, rows: number): Box | null {
  if (points.length < 4 || columns <= 0 || rows <= 0 || !points.every(Number.isFinite)) return null;
  const [ax, ay, bx, by] = points;
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  const x0 = clamp(Math.min(ax, bx) / columns);
  const y0 = clamp(Math.min(ay, by) / rows);
  const x1 = clamp(Math.max(Math.max(ax, bx), Math.min(ax, bx) + 1) / columns);
  const y1 = clamp(Math.max(Math.max(ay, by), Math.min(ay, by) + 1) / rows);
  return x1 > x0 && y1 > y0 ? [x0, y0, x1, y1] : null;
}

/** Percent change from `baseline` to `value` (one decimal), or null when either is unknown. */
export function changePct(baseline: number | null, value: number | null): number | null {
  if (baseline === null || value === null || baseline <= 0) return null;
  return Math.round(((value - baseline) / baseline) * PERCENT * 10) / 10;
}

/** "+12.5 %" / "−3.0 %" / "0.0 %". */
export function formatChange(pct: number | null): string {
  if (pct === null) return '—';
  if (pct === 0) return '0.0 %';
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)} %`;
}
